import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { motion } from "framer-motion";
import {
  ArrowRight,
  Sparkles,
  Zap,
  CheckCircle2,
  TrendingUp,
  Globe,
  Award
} from "lucide-react";
import FAQSection from "../components/FAQSection";
import { homeFaqs } from "../data/faqs";
import BlogSection from "../components/BlogSection"; // adjust path if needed
/* FAQ Accordion Component */
const FAQAccordion = ({ faqs }) => {
  const [openIndex, setOpenIndex] = useState(null);

  const toggleFAQ = (index) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <div className="space-y-4">
      {faqs.map((faq, index) => (
        <div
          key={index}
          className="border border-[#E2E8F0] rounded-xl bg-card shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
        >
          <button
            onClick={() => toggleFAQ(index)}
            className="w-full flex justify-between items-center text-left p-6"
          >
            <span className="text-lg md:text-xl font-medium text-foreground">
              {faq.question}
            </span>

            <span className="text-blue-600 text-2xl">
              {openIndex === index ? "−" : "+"}
            </span>
          </button>

          {openIndex === index && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: "auto" }}
              transition={{ duration: 0.3 }}
              className="px-6 pb-6"
            >
              <p className="text-muted-foreground leading-relaxed">
                {faq.answer}
              </p>
            </motion.div>
          )}
        </div>
      ))}
    </div>
  );
};

export const Home = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const stats = [
    { label: "Projects Delivered", value: "500+" },
    { label: "Happy Clients", value: "150+" },
    { label: "Global Reach", value: "20+ Countries" },
    { label: "Awards Won", value: "12+" }
  ];

  const services = [
    {
      icon: Sparkles,
      title: "Web Development",
      description:
        "Custom websites, SaaS platforms, and enterprise applications"
    },
    {
      icon: Zap,
      title: "Mobile Apps",
      description: "Native and cross-platform apps for iOS and Android"
    },
    {
      icon: CheckCircle2,
      title: "UI/UX Design",
      description: "Intuitive interfaces and delightful user experiences"
    },
    {
      icon: TrendingUp,
      title: "Digital Transformation",
      description: "Modernize systems and processes for growth"
    },
    {
      icon: Globe,
      title: "Cloud Solutions",
      description: "Scalable cloud infrastructure and DevOps practices"
    },
    {
      icon: Award,
      title: "Consulting & Strategy",
      description: "Expert guidance for technology and product decisions"
    }
  ];

  const whyUsItems = [
    {
      icon: CheckCircle2,
      text: "Proven track record with 500+ successful projects"
    },
    {
      icon: TrendingUp,
      text: "Agile methodology for faster time-to-market"
    },
    {
      icon: Globe,
      text: "Global team of expert developers"
    },
    {
      icon: Award,
      text: "98% client satisfaction and retention rate"
    }
  ];

  const handleSubmit = (e) => {
    e.preventDefault();
    setLoading(true);

    setTimeout(() => {
      setLoading(false);
      setSubmitted(true);
    }, 1500);
  };

  return (
    <>
      <Helmet>
        <title>Novaseeds Technologies | Full-Stack Software Development</title>
        <meta
          name="description"
          content="Novaseeds Technologies builds web, mobile, and enterprise solutions trusted by companies worldwide."
        />
      </Helmet>

      <div className="min-h-screen bg-background">

        {/* HERO */}
        <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-blue-600/10 via-blue-400/5 to-transparent"></div>

          <div
            className="absolute inset-0"
            style={{
              backgroundImage:
                "url('https://images.unsplash.com/photo-1687392946857-96c2b7f94b0d')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              opacity: 0.06
            }}
          ></div>

          <div className="container mx-auto px-6 max-w-7xl relative z-10 pt-32 pb-20">

            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center max-w-5xl mx-auto"
            >
              <div className="inline-flex items-center space-x-2 bg-blue-50 border border-blue-200 rounded-full px-6 py-2 mb-8">
                <Sparkles size={16} className="text-blue-600" />
                <span className="text-blue-700 text-sm font-medium">
                  Trusted by 150+ Global Companies
                </span>
              </div>

              <h1 className="text-5xl md:text-7xl font-bold text-foreground mb-6">
                Transform Ideas Into
                <br />
                <span className="text-gradient">
                  Powerful Digital Solutions
                </span>
              </h1>

              <p className="text-lg text-muted-foreground max-w-3xl mx-auto mb-10">
                We build custom web & mobile applications, SaaS platforms,
                and enterprise solutions that drive real business growth.
              </p>

              <div className="flex gap-4 justify-center">
                <a
                  href="#contact"
                  className="bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-3 font-semibold flex items-center gap-2 shadow-[0_2px_8px_rgba(37,99,235,0.25)]"
                >
                  Start Your Project <ArrowRight size={18} />
                </a>
                <a
                  href="https://calendly.com/nvseeds/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-white hover:bg-[#EFF6FF] text-blue-700 border border-blue-200 rounded-full px-8 py-3 font-semibold flex items-center gap-2 shadow-[0_2px_8px_rgba(0,0,0,0.05)]"
                >
                  Book an Appointment <ArrowRight size={18} />
                </a>
              </div>

              {/* STATS */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mt-20">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-4xl font-bold text-gradient">
                      {stat.value}
                    </div>
                    <div className="text-muted-foreground text-sm">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </section>

        {/* TRANSFORMATION PARTNER */}
        <section className="py-16 md:py-24 bg-background border-b border-[#E2E8F0]/60">
          <div className="container mx-auto px-6 max-w-7xl">
            <div className="grid md:grid-cols-12 gap-12 lg:gap-16 items-start">
              <div className="md:col-span-6 flex flex-col font-sans">
                <p className="text-xs md:text-sm font-medium uppercase tracking-[0.2em] text-muted-foreground mb-6">
                  Custom Software &amp; Digital Transformation Partner
                </p>
                <h2 className="text-[32px] font-semibold text-foreground leading-snug mb-10">
                  NovaSeeds Technologies is a technology-driven company delivering
                  end-to-end digital solutions to accelerate business growth.
                </h2>
                <a
                  href="https://calendly.com/nvseeds/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-semibold uppercase tracking-wide text-blue-600 hover:text-blue-700 hover:underline w-fit"
                >
                  BOOK AN APPOINTMENT →
                </a>
              </div>

              <div className="md:col-span-6 space-y-6 pt-2 md:pt-1">
                <p className="text-muted-foreground leading-relaxed">
                  We specialize in AI-powered solutions, intelligent AI agents,
                  custom software development, and seamless third-party
                  integrations. As a trusted technology partner, we help businesses
                  automate workflows, enhance decision-making, and maximize ROI.
                </p>
                <p className="text-muted-foreground leading-relaxed">
                  From startups to enterprise organizations, NVSeeds delivers
                  scalable, secure, and high-performance solutions tailored to your
                  needs. Our full-cycle development approach ensures compliance,
                  data security, and exceptional user experience—driving long-term
                  success for your digital initiatives.
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* SERVICES */}
        <section id="services" className="py-24">
          <div className="container mx-auto px-6 max-w-7xl">

            <div className="text-center mb-16">
              <h2 className="text-4xl text-foreground font-semibold">
                Our Services
              </h2>
            </div>

            <div className="grid md:grid-cols-3 gap-8">
              {services.map((service, index) => {
                const Icon = service.icon;

                return (
                  <div
                    key={index}
                    className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-8 rounded-3xl"
                  >
                    <Icon size={28} className="text-blue-600 mb-4" />

                    <h3 className="text-xl text-foreground mb-3">
                      {service.title}
                    </h3>

                    <p className="text-muted-foreground">
                      {service.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* WHY US */}
        <section className="py-24 bg-[#F8FAFC]">
          <div className="container mx-auto px-6 max-w-7xl">

            <div className="grid md:grid-cols-2 gap-16 items-center">

              <div>
                <h2 className="text-4xl text-foreground mb-6">
                  Why Choose Us
                </h2>

                <div className="space-y-6">
                  {whyUsItems.map((item, index) => {
                    const Icon = item.icon;

                    return (
                      <div key={index} className="flex gap-4">
                        <Icon className="text-blue-600" />
                        <p className="text-muted-foreground">
                          {item.text}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>

              <img
                src="https://images.unsplash.com/photo-1630673470267-417e4d361129"
                className="rounded-3xl"
                alt="team"
              />
            </div>
          </div>
        </section>

        {/* BOOK APPOINTMENT */}
        <section className="py-16 bg-[#F8FAFC]">
          <div className="container mx-auto px-6 max-w-4xl">
            <div className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] rounded-3xl p-10 text-center">
              <h2 className="text-3xl md:text-4xl text-foreground mb-4">
                Book an Appointment
              </h2>
              <p className="text-muted-foreground mb-8">
                Schedule a quick 30-minute consultation with our team to discuss
                your project goals.
              </p>
              <a
                href="https://calendly.com/nvseeds/30min"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white rounded-full px-8 py-3 font-semibold shadow-[0_2px_8px_rgba(37,99,235,0.25)]"
              >
                Book Appointment <ArrowRight size={18} />
              </a>
            </div>
          </div>
        </section>

        {/* CONTACT */}
        <section id="contact" className="py-24">
          <div className="container mx-auto px-6 max-w-4xl">

            <div className="text-center mb-12">
              <h2 className="text-4xl text-foreground">
                Let's Build Something Amazing
              </h2>
            </div>

            <div className="bg-card border border-[#E2E8F0] shadow-[0_2px_8px_rgba(0,0,0,0.05)] p-10 rounded-3xl">

              {submitted ? (
                <div className="text-center">
                  <CheckCircle2
                    size={40}
                    className="text-green-600 mx-auto mb-4"
                  />
                  <p className="text-muted-foreground">
                    Thank you! We'll contact you soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleSubmit}>

                  <input
                    className="w-full mb-4 bg-background border border-[#CBD5E1] p-3 rounded-lg text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none"
                    placeholder="Name"
                    required
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                  />

                  <input
                    className="w-full mb-4 bg-background border border-[#CBD5E1] p-3 rounded-lg text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none"
                    placeholder="Email"
                    required
                    type="email"
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                  />

                  <textarea
                    className="w-full mb-4 bg-background border border-[#CBD5E1] p-3 rounded-lg text-foreground placeholder:text-muted-foreground focus:border-blue-600 focus:ring-1 focus:ring-blue-600 focus:outline-none"
                    rows="5"
                    placeholder="Project Details"
                    required
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                  />

                  <button
                    type="submit"
                    className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-full shadow-[0_2px_8px_rgba(37,99,235,0.25)]"
                  >
                    {loading ? "Sending..." : "Send Message"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </section>

        {/* FAQ */}
       <FAQSection faqs={homeFaqs} title="Frequently Asked Questions" />
{/* Blog Section */}
<BlogSection categorySlug="trends" title="Market Trends " maxPosts={3} />
      </div>
    </>
  );
};
